import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { workspaceMembersQueryOptions } from '@/lib/api/workspaces.js';
import { buildSpeakerDirectory } from '@/ui/chat/speaker.js';

/**
 * Turning an artifact's `created_by` into something a human can read. The registry stores an internal
 * identity id, which is a UUID — so every surface that shows "who made this" has to resolve it through
 * the workspace member directory the screen already fetches.
 */

/**
 * Shown for an id the directory cannot name: a member who has left the workspace, or a non-member
 * identity. A shortened UUID would be just as meaningless to the user, and the full one is what this fix
 * exists to get off the screen.
 */
export const UNKNOWN_ARTIFACT_AUTHOR = 'Unknown member';

/**
 * The display name for one author id. `directory` is keyed by every id space a member can appear
 * under — see {@link buildSpeakerDirectory} — so it does not matter whether the registry stamped the
 * internal user id or the Entra oid.
 */
export function artifactAuthorName(
  userId: string,
  directory: ReadonlyMap<string, string>,
): string {
  return directory.get(userId) ?? UNKNOWN_ARTIFACT_AUTHOR;
}

/**
 * A resolver for author ids in one workspace, backed by the member directory. The directory is a shared
 * query (`/v1/workspaces/:id/members`, 30s stale time) that the artifacts route and the chat already
 * read, so this adds no endpoint and normally no request — it subscribes to what is cached.
 */
export function useArtifactAuthorName(
  workspaceId: string,
): (userId: string) => string {
  const membersQuery = useQuery({
    ...workspaceMembersQueryOptions(workspaceId),
    enabled: workspaceId.length > 0,
  });
  const directory = useMemo(
    () => buildSpeakerDirectory(membersQuery.data ?? []),
    [membersQuery.data],
  );
  return useCallback(
    (userId: string) => artifactAuthorName(userId, directory),
    [directory],
  );
}
